import {FC, useState, useEffect, useMemo} from 'react';
import {Box, Typography, Tab, Tabs, Badge, useMediaQuery} from '@mui/material';
import {ReactComponent as ChecksIcon} from '@icons/checks.svg';
import {ReactComponent as NotificationIcon} from '@/assets/icons/notification-bell.svg';
import {
  defaultPage,
  defaultPageSize,
  H6,
  MARK_ALL_AS_READ,
  OUTLINE,
  PRIMARY,
  SECONDARY,
  SUBTITLE2,
} from '@/utils/constants';
import NotificationItem from '@/components/Organization/Notifications/NotificationItem';
import Button from '@/shared/components/Button';
import EmptyContent from '@/shared/components/Table/EmptyContent';
import {INotification, TabType} from '@/utils/types';
import {getGroupedNotificationsByDate} from '@/utils/helpers/notifications';
import {allNotificationSelector, getAllNotifications, unreadNotificationSelector} from '@/store/notification';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {notificationsPageStyle} from './Notifications.styles';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {useGetOrganizationId} from '@/utils/hooks';
import {getUserIdSelector} from '@/store/user';
import {getCurrentEventSelector} from '@/store/organization-event';
import {NotificationService} from '@/utils/services/notification.service';
import {getOrganizationSelector} from '@/store/organization';
import Pagination from '@/shared/components/Pagination';
import Loader from '@/shared/components/Loader';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {USER_TYPE} from '@/utils/enums';
import {getSuperAdminSelector} from '@/store/admin';

const NotificationsPage: FC = () => {
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState<'all' | 'required'>('required');
  const [currentPage, setCurrentPage] = useState<number>(defaultPage);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);

  const organizationId = useGetOrganizationId();
  const {role, apiInstance} = useApiInstance();

  const userId = useAppSelector(getUserIdSelector);
  const organization = useAppSelector(getOrganizationSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const admin = useAppSelector(getSuperAdminSelector);

  const {
    notifications: unreadNotifications,
    totalCount: unreadTotalCount,
    pageSize: unreadPageSize,
  } = useAppSelector(unreadNotificationSelector);
  const {
    notifications: allNotifications,
    totalCount: allTotalCount,
    pageSize: allPageSize,
  } = useAppSelector(allNotificationSelector);

  const notifications = activeTab === 'required' ? unreadNotifications : allNotifications;
  const totalCount = activeTab === 'required' ? unreadTotalCount : allTotalCount;
  const pageSize = activeTab === 'required' ? unreadPageSize : allPageSize;

  const hasAnyNotifications = allTotalCount > 0 || unreadTotalCount > 0;

  const isLaptop = useMediaQuery('(min-width:1200px)');

  const roleId = (() => {
    switch (role) {
      case USER_TYPE.ORGANIZATION:
        return organization?.ownerUserId;
      case USER_TYPE.ADMIN:
        return admin?.id;
      default:
        return userId;
    }
  })();

  const isReadyToFetch = !!roleId && (role === USER_TYPE.ADMIN || !!currentEvent?.id);

  const handleMarkAllAsRead = async () => {
    try {
      if (role === USER_TYPE.USER && userId) {
        await NotificationService.updateAllNotificationsUserStatus(userId);
      } else if (role === USER_TYPE.ORGANIZATION && organizationId && currentEvent?.id) {
        const data = {
          organizationId,
          eventId: currentEvent.id,
          isOrganization: true,
        };
        await NotificationService.updateAllNotificationsOrganizationStatus(data, apiInstance);
      } else if (role === USER_TYPE.ADMIN) {
        const data = {
          userId: admin?.id,
          isOrganization: false,
        };
        await NotificationService.updateAllNotificationsOrganizationStatus(data, apiInstance);
      }

      if (currentPage !== defaultPage) {
        setCurrentPage(defaultPage);
      } else {
        await getNotificationData();
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const changeTab = (_: React.SyntheticEvent, newTab: TabType) => {
    setActiveTab(newTab);
    setCurrentPage(defaultPage);
  };

  const groupedNotificationsByDate = useMemo(() => getGroupedNotificationsByDate(notifications), [notifications]);

  const handleNotificationClick = async (item: INotification) => {
    if (item.isRead) return;

    try {
      await NotificationService.updateOneNotificationStatus(item.id, apiInstance);
      getNotificationData();

      if (activeTab !== 'required') {
        const data = {
          page: 1,
          pageSize: 10,
          isOrderDesc: true,
          ...(currentEvent?.id && {eventId: currentEvent.id}),
          isOrganization: role === USER_TYPE.ORGANIZATION,
          userId: roleId,
          isRead: false,
          ...(role === USER_TYPE.ORGANIZATION && {organizationId}),
        };
        dispatch(getAllNotifications({data, apiInstance})).catch(() => {});
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const getNotificationData = async () => {
    if (!isReadyToFetch) return;
    // if (!roleId) return;

    try {
      setIsDataLoading(true);

      const data = {
        page: isLaptop ? currentPage : 1,
        pageSize: isLaptop ? defaultPageSize : 10000,
        isOrderDesc: true,
        ...(currentEvent?.id && {eventId: currentEvent.id}),
        isOrganization: role === USER_TYPE.ORGANIZATION,
        userId: roleId,
        ...(activeTab === 'required' && {isRead: false}),
        ...(role === USER_TYPE.ORGANIZATION && {organizationId}),
      };

      await dispatch(getAllNotifications({data, apiInstance})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    getNotificationData();
  }, [currentEvent?.id, activeTab, currentPage, roleId]);

  // Первинний "all" фетч — заповнює allNotificationData, звідки hasAnyNotifications
  useEffect(() => {
    if (!isReadyToFetch) return;
    // if (!roleId) return;

    const data = {
      page: 1,
      pageSize: 1,
      isOrderDesc: true,
      ...(currentEvent?.id && {eventId: currentEvent.id}),
      isOrganization: role === USER_TYPE.ORGANIZATION,
      userId: roleId,
      ...(role === USER_TYPE.ORGANIZATION && {organizationId}),
    };

    dispatch(getAllNotifications({data, apiInstance})).catch((error) => {
      toast.error(getErrorMessage(error));
    });
  }, [currentEvent?.id, roleId]);

  return (
    <Box sx={{position: 'relative'}}>
      {isDataLoading ? (
        <Loader parentClassName={notificationsPageStyle.loader} size={50} />
      ) : hasAnyNotifications ? (
        <Box sx={notificationsPageStyle.wrapper}>
          <Box sx={notificationsPageStyle.wrapperTitle}>
            <Typography variant={H6} sx={notificationsPageStyle.title}>
              Notifications
            </Typography>
            {activeTab === 'required' && (
              <Button
                endIcon={<ChecksIcon />}
                title={MARK_ALL_AS_READ}
                variant={OUTLINE}
                color={PRIMARY}
                disableRipple
                sx={notificationsPageStyle.button}
                onClick={handleMarkAllAsRead}
                disabled={!unreadTotalCount}
              />
            )}
          </Box>

          <Box sx={notificationsPageStyle.tabsWrapper}>
            <Tabs value={activeTab} onChange={changeTab} indicatorColor={SECONDARY} sx={notificationsPageStyle.tabs}>
              <Tab
                disableRipple
                value="required"
                label={
                  <Box>
                    <Typography variant={SUBTITLE2} sx={{display: 'flex', gap: '20px'}}>
                      New Notifications
                      <Badge sx={notificationsPageStyle.badgeIcon} badgeContent={unreadTotalCount} showZero={false} />
                    </Typography>
                  </Box>
                }
              />
              <Tab
                disableRipple
                value="all"
                label={
                  <Box>
                    <Typography variant={SUBTITLE2}>All notifications </Typography>
                  </Box>
                }
              />
            </Tabs>
          </Box>
          {notifications.length > 0 ? (
            <>
              <Box sx={notificationsPageStyle.wrapperList}>
                {Object.entries(groupedNotificationsByDate).map(([group, items]) => (
                  <Box key={group} sx={notificationsPageStyle.groupWrapper}>
                    <Typography sx={notificationsPageStyle.group}>{group}</Typography>
                    {(items as INotification[]).map((item) => (
                      <NotificationItem key={item.id} notificationItem={item} handleClick={handleNotificationClick} />
                    ))}
                  </Box>
                ))}
              </Box>

              <Box sx={notificationsPageStyle.pagination}>
                <Pagination
                  page={currentPage}
                  pageSize={pageSize}
                  total={totalCount || 0}
                  onChange={(step) => setCurrentPage(step)}
                />
              </Box>
            </>
          ) : (
            <Box sx={notificationsPageStyle.wrapperList}>
              <Typography>No new notifications</Typography>
            </Box>
          )}
        </Box>
      ) : (
        <EmptyContent title="You do not have any notifications." icon={NotificationIcon} />
      )}
    </Box>
  );
};

export default NotificationsPage;
